import { z } from 'zod';
import { idParamSchema, uuidSchema } from '../../common/schemas/id.schemas.js';
import { amountSchema } from '../../common/schemas/money.schemas.js';
/**
 * The public depreciation simulator (`/simulator`): a testing tool, open without a session, that prices
 * a car on a proposed set of factors and lets the team save each evaluation for the Excel export.
 *
 * The browser does the arithmetic (`used-vehicle-engine`'s `simulator` module) and sends the whole
 * picture — the entries, the figures used, which figures differ from the defaults, and the result — so a
 * saved row is complete on its own and the export never has to re-price anything.
 */
export const depreciationCategorySchema = z.enum(['low', 'medium', 'high']);
export const brandEquitySchema = z.enum(['vhigh', 'high', 'medium', 'low', 'vlow']);
export const simulatorGradeSchema = z.enum(['G7', 'G4', 'G3', 'G2', 'G1', 'G0']);
export const simulatorSeveritySchema = z.enum(['EXCELLENT', 'MINOR', 'MODERATE', 'MAJOR', 'SEVERE']);
export const inspectionTableSchema = z.enum(['standard', 'luxury']);
const optionKeySchema = z.string().trim().min(1).max(40);
/** A rate or a one-off adjustment, as a fraction; negative for a deduction. */
const rateSchema = z.number().finite().min(-1).max(1);
const nonNegativeSchema = z.number().finite().nonnegative().max(99_999_999);
const ladderSchema = z.array(z.number().finite().min(0).max(0.95)).length(7);
export const simulatorInputsSchema = z.object({
    preset: z.string().trim().max(40).nullable(),
    brand: z.string().trim().min(1).max(80),
    model: z.string().trim().min(1).max(120),
    equity: brandEquitySchema,
    cat: depreciationCategorySchema,
    agency: optionKeySchema,
    price: amountSchema,
    fuel: optionKeySchema,
    trim: optionKeySchema,
    demand: optionKeySchema,
    supply: optionKeySchema,
    age: z.number().int().min(1).max(7),
    odometer: z.number().finite().nonnegative().max(9_999_999),
    owners: z.number().int().min(0).max(99),
    usage: optionKeySchema,
    specs: optionKeySchema,
    warranty: z.boolean(),
    facelift: z.boolean(),
    customs: z.boolean(),
    grade: simulatorGradeSchema,
    sev: z.object({
        interior: simulatorSeveritySchema,
        body: simulatorSeveritySchema,
        engine: simulatorSeveritySchema,
        hybrid: simulatorSeveritySchema,
        tires: simulatorSeveritySchema,
    }),
    dmg1: z.number().int().min(0).max(6),
    dmg2: z.number().int().min(0).max(6),
    service: optionKeySchema,
    table: inspectionTableSchema,
});
const pointsOptionSchema = z.object({
    key: optionKeySchema,
    name: z.string().trim().min(1).max(80),
    ex: z.string().max(160).optional(),
    pts: rateSchema,
});
const fuelOptionSchema = pointsOptionSchema.extend({ hv: z.boolean() });
const percentOptionSchema = z.object({
    key: optionKeySchema,
    name: z.string().trim().min(1).max(80),
    pct: rateSchema,
});
const gradeTableSchema = z.object({ G7: rateSchema, G4: rateSchema, G3: rateSchema, G2: rateSchema, G1: rateSchema, G0: rateSchema });
/** Every editable figure of the page, exactly as the engine's `SimulatorValues` lays them out. */
export const simulatorValuesSchema = z.object({
    cat: z.object({ low: ladderSchema, medium: ladderSchema, high: ladderSchema }),
    spread: z.array(z.number().finite().min(0).max(1)).max(7),
    brand: z.array(pointsOptionSchema).max(10),
    fuel: z.array(fuelOptionSchema).max(10),
    usage: z.array(percentOptionSchema).max(10),
    demand: z.array(percentOptionSchema).max(10),
    supply: z.array(percentOptionSchema).max(10),
    specs: z.array(percentOptionSchema).max(10),
    warranty: rateSchema,
    agency: z.array(percentOptionSchema).max(10),
    service: z.array(percentOptionSchema).max(10),
    grade: z.object({ standard: gradeTableSchema, luxury: gradeTableSchema }),
    sev: z.object({ EXCELLENT: rateSchema, MINOR: rateSchema, MODERATE: rateSchema, MAJOR: rateSchema, SEVERE: rateSchema }),
    km: z.object({ allowance: nonNegativeSchema, step: nonNegativeSchema, rate: rateSchema }),
    owners: z.object({ allowance: z.number().int().min(0).max(99), rate: rateSchema }),
    facelift: rateSchema,
    customs: rateSchema,
    rounding: z.number().finite().positive().max(100_000),
    worst: z.object({
        grade: simulatorGradeSchema,
        sev: simulatorSeveritySchema,
        service: optionKeySchema,
        kmPerYear: nonNegativeSchema,
    }),
});
export const simulatorChangeSchema = z.object({
    path: z.string().trim().min(1).max(80),
    label: z.string().trim().min(1).max(160),
    unit: z.enum(['%', 'pts', 'km', 'JOD', 'owners', 'share', '']),
    defaultValue: z.union([z.number().finite(), z.string().max(80)]),
    usedValue: z.union([z.number().finite(), z.string().max(80)]),
});
const categoryPricesSchema = z.object({ best: amountSchema, car: amountSchema, worst: amountSchema });
export const simulatorOutputsSchema = z.object({
    simulatorPrice: amountSchema,
    bestPrice: amountSchema,
    worstPrice: amountSchema,
    left: z.number().finite().min(0).max(10),
    bestLeft: z.number().finite().min(0).max(10),
    worstLeft: z.number().finite().min(0).max(10),
    blocked: z.boolean(),
    byCategory: z.object({ low: categoryPricesSchema, medium: categoryPricesSchema, high: categoryPricesSchema }),
});
export const saveSimulatorEstimateInputSchema = z.object({
    inputs: simulatorInputsSchema,
    values: simulatorValuesSchema,
    /** Every figure that differs from the page's defaults; flagged in the export. */
    changes: z.array(simulatorChangeSchema).max(200),
    outputs: simulatorOutputsSchema,
    /** The deal the evaluation belongs to: the car's VIN and the customer, plus who owns the deal. */
    vin: z.string().trim().min(1).max(32),
    customerName: z.string().trim().min(1).max(120),
    dealOwner: z.string().trim().max(120).nullable(),
    /** The observed market price the error margin is measured against, when known. */
    preownedPrice: amountSchema.nullable(),
    remarks: z.string().trim().max(2000).nullable(),
});
/**
 * A saved row. The deal fields are looser than on `save`: rows saved before the fields existed carry empty
 * strings, and a list that refused to serialise them would hide every earlier evaluation.
 */
export const simulatorEstimateSchema = saveSimulatorEstimateInputSchema.omit({ vin: true, customerName: true }).extend({
    id: uuidSchema,
    createdAt: z.string().datetime(),
    vin: z.string().max(32),
    customerName: z.string().max(120),
});
export const listSimulatorEstimatesOutputSchema = z.object({
    items: z.array(simulatorEstimateSchema),
});
export const removeSimulatorEstimateInputSchema = idParamSchema;
export const removeSimulatorEstimateOutputSchema = z.object({
    removed: z.boolean(),
});
//# sourceMappingURL=simulator.schemas.js.map